import * as events from '../base/constant';
import { AutoFormatPlugin } from '../../editor-manager/plugin/autoformat';
import { isNullOrUndefined, initializeTelemetryFeature } from '@syncfusion/ej2-base';
/**
 * AutoFormat module provides functionality for autoformatting text in the Rich Text Editor.
 * Handles automatic formatting of Markdown patterns (e.g., **bold**, ~~strikethrough~~, `code`).
 *
 * @description Manages autoformatting logic within the Rich Text Editor.
 * @export
 */
export class AutoFormat {
    /**
     * Creates an instance of auto format module
     *
     * @param {IRichTextEditor} parent - The parent Rich Text Editor instance
     * @returns {void}
     */
    constructor(parent) {
        initializeTelemetryFeature('AutoFormat', 'RichTextEditor');
        this.parent = parent;
        this.isDestroyed = false;
        this.addEventListener();
    }
    /* Registers event handlers for auto format operations */
    addEventListener() {
        this.parent.on(events.keyUp, this.onKeyUp, this);
        this.parent.on(events.destroy, this.destroy, this);
        this.parent.on(events.bindOnEnd, this.bindOnEnd, this);
    }
    /* Handles key up events for space keys */
    onKeyUp(e) {
        let block = false;
        if (e.args.which === 32 && this.parent.formatter.editorManager.autoFormatObj) {
            const range = this.parent.formatter.editorManager.nodeSelection.getRange(this.parent.inputElement.ownerDocument);
            const isValidBlock = this.autoFormatObj.findBlockAutoFormatCommandInRange(range);
            if (!isNullOrUndefined(isValidBlock)) {
                const args = { item: { command: isValidBlock.format, subCommand: isValidBlock.tag } };
                if (isValidBlock.tag === 'hr') {
                    args.item.value = '<hr/>';
                }
                if (isValidBlock.format === 'CodeBlock') {
                    const languages = this.parent.codeBlockSettings.languages;
                    const defaultLanguage = this.parent.codeBlockSettings.defaultLanguage;
                    let language;
                    let label;
                    for (let i = 0; i < languages.length; i++) {
                        if (languages[i].language === defaultLanguage) {
                            language = languages[i].language;
                            label = languages[i].label;
                            break;
                        }
                    }
                    this.parent.formatter.process(this.parent, args, e.args, { language: language, label: label, action: 'createCodeBlock', enterAction: this.parent.enterKey });
                    block = true;
                }
                else {
                    this.parent.formatter.process(this.parent, args, e.args);
                    block = true;
                }
            }
        }
        if (['*', '_', '`', '~'].indexOf(e.args.key) !== -1) {
            const range = this.parent.formatter.editorManager.nodeSelection.getRange(this.parent.inputElement.ownerDocument);
            if (this.autoFormatObj.findAutoFormatCommandInRange(range) && !block) {
                const args = { item: { command: 'autoformat', subCommand: 'inlinestyle' } };
                this.parent.formatter.process(this.parent, args, e.args);
            }
        }
    }
    /* Removes all event listeners attached by this module
     * Unregisters handlers to prevent memory leaks
     */
    removeEventListener() {
        this.parent.off(events.keyUp, this.onKeyUp);
        this.parent.off(events.destroy, this.destroy);
        this.parent.off(events.bindOnEnd, this.bindOnEnd);
    }
    /**
     * Cleans up resources and detaches event handlers when the component is destroyed
     *
     * @returns {void}
     */
    destroy() {
        if (this.isDestroyed) {
            return;
        }
        this.isDestroyed = true;
        this.removeEventListener();
    }
    /**
     * For internal use only - Get the module name.
     *
     * @returns {string} - returns the string value
     */
    getModuleName() {
        return 'autoFormat';
    }
    /**
     * Initializes the AutoFormatPlugin object in the editor manager after editor initialization is complete.
     * This method binds the auto format module to the editor's formatter for handling auto format related operations.
     *
     * @returns {void} - This method does not return a value
     * @private
     */
    bindOnEnd() {
        if (!this.parent.formatter.editorManager.autoFormatObj) {
            this.parent.formatter.editorManager.autoFormatObj = new AutoFormatPlugin(this.parent.formatter.editorManager);
            this.autoFormatObj = this.parent.formatter.editorManager.autoFormatObj;
        }
    }
}
